'use client';

import { type MouseEvent, type TouchEvent, useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { useRouter } from 'next/navigation';
import { Moon, Sun } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';
import { useMetaColor } from '@/hooks/use-meta-color';
import { Button } from '@/components/ui/button';
import Logo from '@/components/logo';

const navItems = ['Inicio', 'Seguimiento', 'Módulos', 'Funciones', 'Precio', 'FAQ', 'Contacto'];

// ASCII section id from a nav label ('Módulos' → 'modulos'). Keeps anchors out
// of URL-encoding and matches the section `id`s.
const slug = (item: string) =>
  item
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-');

const Header = () => {
  const router = useRouter();
  const { resolvedTheme, setTheme } = useTheme();
  const { metaColor, setMetaColor } = useMetaColor();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const [mounted, setMounted] = useState(false);

  const isActiveItem = (item: string) => activeSection === slug(item);

  const goToSection = (item: string) => {
    setIsOpen(false);
    const targetId = slug(item);
    const element = document.getElementById(targetId);
    setActiveSection(targetId);

    setTimeout(() => {
      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - 50;
        router.push(`#${targetId}`, { scroll: false });
        window.scrollTo({ top: y, behavior: 'instant' });
      } else if (item === 'Inicio') {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    }, 50);
  };

  useEffect(() => {
    setMounted(true);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      if (window.scrollY < 50) setActiveSection('inicio');

      const scrollPosition = window.scrollY + 200;

      for (const item of navItems) {
        const element = document.getElementById(slug(item));

        if (element) {
          // Absolute document top — not offsetTop, which is relative to the
          // offsetParent and breaks for sections nested in positioned wrappers
          // (e.g. Precio inside the sticky Prensa→Precio slide-over).
          const top = element.getBoundingClientRect().top + window.scrollY;

          if (scrollPosition >= top && scrollPosition < top + element.offsetHeight) {
            setActiveSection((prev) => (prev !== slug(item) ? slug(item) : prev));

            return;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keep the browser UI (theme-color) in sync with the resolved theme.
  useEffect(() => {
    setMetaColor(metaColor);
  }, [metaColor, setMetaColor]);

  // Lock body scroll while the mobile overlay is open.
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const navigateToBackoffice = (
    event: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>,
  ) => {
    (event as MouseEvent<HTMLButtonElement>).preventDefault();
    track('login_click', { location: 'header' });
    window.open('https://backoffice.mendly.com.ar/login', '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={cn(
          'fixed top-0 right-0 left-0 z-50 transition-all duration-300',
          isScrolled || isOpen
            ? 'border-b border-line bg-background/70 shadow-xs backdrop-blur-md'
            : 'border-b border-transparent bg-transparent',
        )}
      >
        <div className="mx-auto flex w-full max-w-[var(--container)] items-center justify-between px-6 py-4">
          <Logo />

          <div className="flex items-center gap-2.5">
            {/* Desktop nav */}
            <nav className="hidden items-center space-x-8 lg:flex">
              {navItems.map((item, index) => (
                <motion.a
                  key={item}
                  href={`#${slug(item)}`}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (index + 2) * 0.1 }}
                  className={cn(
                    'group relative cursor-pointer font-semibold transition-colors',
                    isActiveItem(item) ? 'text-brand' : 'text-ink hover:text-brand',
                  )}
                >
                  {item}
                  <span
                    className={cn(
                      'absolute -bottom-1 left-0 h-0.5 bg-brand transition-all',
                      isActiveItem(item) ? 'w-full' : 'w-0 group-hover:w-full',
                    )}
                  />
                </motion.a>
              ))}

              <Button className="font-semibold" onClick={navigateToBackoffice} variant="brand">
                Ingresar
              </Button>
            </nav>

            {/* Theme toggle */}
            {mounted && (
              <Button
                className="size-11 cursor-pointer text-ink-soft hover:bg-transparent hover:text-ink"
                variant="ghost"
                size="icon"
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                aria-label="Cambiar tema"
              >
                {resolvedTheme === 'dark' ? (
                  <Sun className="size-4" />
                ) : (
                  <Moon className="size-4" />
                )}
              </Button>
            )}

            {/* Hamburger (mobile) */}
            <button
              type="button"
              onClick={() => setIsOpen((o) => !o)}
              className="flex size-11 flex-col items-center justify-center gap-1.5 text-ink lg:hidden"
              aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={isOpen}
            >
              <motion.span
                animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                className="block h-0.5 w-6 origin-center rounded-full bg-current"
              />
              <motion.span
                animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
                className="block h-0.5 w-6 rounded-full bg-current"
              />
              <motion.span
                animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                className="block h-0.5 w-6 origin-center rounded-full bg-current"
              />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile full-screen overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 flex flex-col bg-paper px-6 pt-24 pb-10 lg:hidden"
          >
            <nav className="flex flex-col">
              {navItems.map((item, i) => (
                <motion.button
                  key={item}
                  type="button"
                  onClick={() => goToSection(item)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i + 0.05, duration: 0.35 }}
                  className="border-b border-line py-4 text-left font-display text-3xl font-bold tracking-[-0.02em] text-ink transition-colors hover:text-brand"
                >
                  {item}
                </motion.button>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.35 }}
              className="mt-auto"
            >
              <Button
                variant="brand"
                size="xl"
                className="w-full"
                onClick={navigateToBackoffice}
                onTouchEnd={navigateToBackoffice}
              >
                Ingresar
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
